import { Box } from './kit'
import { box, cylinder, face, faceBore, posts } from './projection'

/* ==========================================================================
   Screen-printing set: an aluminium print table with register pins, the
   black panel with two knob bores, and the screen — a cast frame on two
   guided hangers, its blue emulsion stencil open where the scales print.
   World units (projection.js). The mesh, coat, fill and V are drawn in the
   mesh face (h 100 when down, origin on the left scale), so their moving
   edges run along the depth like the blades that make them.
   ========================================================================== */

const r2 = (n) => Math.round(n * 100) / 100

/* ---- The printed scale -----------------------------------------------------
   A combi-boiler knob scale as a <g>: arc, ticks, ring and radiator symbol,
   without the numerals and without the knob's own hub and pointer (they
   belong to the knob, not to the panel print). */
const SCALE_R = 38
const TICKS = Array.from({ length: 11 }, (_, i) => 135 + i * 27)
const polar = (r, deg) => [r2(r * Math.cos((deg * Math.PI) / 180)), r2(r * Math.sin((deg * Math.PI) / 180))]
const SCALE_ARC = (() => {
  const [sx, sy] = polar(SCALE_R, 135)
  const [ex, ey] = polar(SCALE_R, 45)
  return `M${sx} ${sy}A${SCALE_R} ${SCALE_R} 0 1 1 ${ex} ${ey}`
})()
const ticks = (major) =>
  TICKS.filter((_, i) => (i % 5 === 0) === major)
    .map((deg) => {
      const [x1, y1] = polar(SCALE_R + 4, deg)
      const [x2, y2] = polar(SCALE_R + (major ? 11 : 8), deg)
      return `M${x1} ${y1}L${x2} ${y2}`
    })
    .join('')
const MAJOR = ticks(true)
const MINOR = ticks(false)

const RING_AND_BARS = 'M23 0A23 23 0 1 0 -23 0A23 23 0 1 0 23 0M-9 51v-9M-4.5 51v-9M0 51v-9M4.5 51v-9'

export function ScaleGlyph({ className, transform }) {
  return (
    <g className={`pp-scale ${className}`} transform={transform}>
      <path d={SCALE_ARC} strokeWidth="3.2" />
      <path d={MAJOR} strokeWidth="2.2" />
      <path d={MINOR} strokeWidth="1.4" />
      <path d={RING_AND_BARS} strokeWidth="2" />
      <path d="M-11 51h15.5" strokeWidth="1.6" />
      <circle className="pp-scale-dot" cx="9.5" cy="43" r="1.6" />
    </g>
  )
}

/* Scale centres on the panel face (h 88, z 100) and on the mesh (h 100). */
const SCALE_X = [678, 998]
const OPENINGS = [0, 320]
const MESH_FACE = 'matrix(1 0 -0.18 0.5 678 550)'

/* ---- Print table: a machined aluminium block on the machine table ------- */

const TABLE = box({ x0: 460, x1: 1200, z0: 12, z1: 184, h0: 0, h1: 80 })
const HOLES = Array.from({ length: 10 }, (_, i) => `M12 ${-16 * (i + 1)}H740`).join('')
const TABLE_SCREWS = [480, 1184]
  .flatMap((x) => [619, 687].map((y) => `M${x + 2.5} ${y}a2.5 2.5 0 1 0 -5 0a2.5 2.5 0 1 0 5 0`))
  .join('')
/* Two machined pockets in the front recess, with socket screws, so the face
   holds up under the tracking shot. */
const POCKETS = [
  [492, 812],
  [852, 1172],
]
const POCKET = POCKETS.map(([a, b]) => `M${a} 634H${b}V670H${a}Z`).join('')
const POCKET_FLOOR = POCKETS.map(([a, b]) => `M${a} 667H${b}V670H${a}Z`).join('')
const POCKET_LIP = POCKETS.map(([a, b]) => `M${a} 670H${b}`).join('')
const POCKET_SCREWS = POCKETS.flatMap(([a, b]) => [a + 14, b - 14])
  .map((x) => `M${x + 3.4} 652a3.4 3.4 0 1 0 -6.8 0a3.4 3.4 0 1 0 6.8 0`)
  .join('')

export function PrintTable() {
  return (
    <g>
      <path className="f-poly" d={TABLE.shadow} />
      <Box geo={TABLE} />
      <path className="f-aS" d="M462.2 624H1202.2V680H462.2Z" />
      <path className="f-gS" d="M462.2 624H1202.2V627.5H462.2Z" />
      <path className="l-spec" d="M462.2 680H1202.2" />
      <path className="f-cC" d={POCKET} />
      <path className="f-aF" d={POCKET_FLOOR} />
      <path className="l-spec" d={POCKET_LIP} />
      <path className="f-aF pp-hair" d={POCKET_SCREWS} />
      <path className="f-gR pp-hair" d={TABLE_SCREWS} />
      <g transform="matrix(1 0 -0.18 0.5 462.16 614)">
        <path className="l-holes" d={HOLES} />
      </g>
    </g>
  )
}

/* Register pins: two behind the panel, one at its left edge (chrome). */
const PINS = {
  back: posts(4, 16, [
    [651.7, 532],
    [1141.7, 532],
  ]),
  side: posts(4, 16, [[510, 570]]),
}

/** `back`: the two pins behind the panel; `side`: the one at its left edge. */
export function Pins({ at }) {
  const pins = PINS[at]
  return (
    <g>
      <path className="f-cB" d={pins.body} />
      <path className="f-cC" d={pins.band} />
      <path className="f-cA" d={pins.top} />
    </g>
  )
}

/* ---- The panel: moulded black, two knob bores, the scales print here ---- */

const PANEL = box({ x0: 500, x1: 1160, z0: 30, z1: 170, h0: 80, h1: 88 })
const BORE = faceBore(14, 8)

/** The panel with its two bores; the scales are printed on it (pp-print). */
export function Panel({ id }) {
  return (
    <g>
      <path className="f-aF" d={PANEL.shadow} />
      <path className="f-pS" d={PANEL.side} />
      <path className="f-pW" d={PANEL.front} />
      <path className="f-pF" d={PANEL.top} />
      <path d={PANEL.top} fill={`url(#${id}stipple)`} />
      <path className="l-glint" d={`${PANEL.litTop}${PANEL.litLeft}`} />
      {SCALE_X.map((x) => (
        <g key={x} transform={face(x, 562)}>
          <path className="f-poly" d={BORE.hole} />
          <path className="f-pW" d={BORE.wall} />
          <ScaleGlyph className="g-scale-ink pp-print" />
        </g>
      ))}
    </g>
  )
}

/* ---- Hangers: guided cylinders under the beam (z 90) lift the screen ---- */

const HANGER = cylinder(14, 108, [[0, 0.24], [0.7, 1]])
const GLAND = cylinder(8, 6, [[0, 0.3]])
const HANGER_X = [456.2, 1236.2]

export function HangerBodies() {
  return HANGER_X.map((x) => (
    <g key={x}>
      <g transform={`translate(${x} 295)`}>
        <path className="f-aF" d={HANGER.body} />
        <path className="f-aT" d={HANGER.bands[0]} />
        <path className="f-aS" d={HANGER.bands[1]} />
      </g>
      <g transform={`translate(${x} 301)`}>
        <path className="f-aS" d={GLAND.body} />
        <path className="f-aF" d={GLAND.bands[0]} />
      </g>
    </g>
  ))
}

function Chrome({ x, y0, y1, w }) {
  return (
    <>
      <rect className="f-cB" x={x - w / 2} y={y0} width={w} height={y1 - y0} />
      <rect className="f-cA" x={x - w / 2} y={y0} width={w * 0.22} height={y1 - y0} />
      <rect className="f-cC" x={x + w * 0.15} y={y0} width={w * 0.35} height={y1 - y0} />
    </>
  )
}

/** Piston rods: they slide out of the hanger bodies with the frame. */
export function HangerRods() {
  return (
    <>
      <g className="pp-hrod-l">
        <Chrome x={HANGER_X[0]} y0={260} y1={539} w={10} />
      </g>
      <g className="pp-hrod-r">
        <Chrome x={HANGER_X[1]} y0={260} y1={539} w={10} />
      </g>
    </>
  )
}

/* ---- The screen frame: cast aluminium bars, the side arms that hold it -- */

const BAR = { h0: 100, h1: 114 }
const FRONT_BAR = box({ x0: 450, x1: 1210, z0: 0, z1: 18, ...BAR })
const BACK_BARS = [
  box({ x0: 450, x1: 468, z0: 18, z1: 162, ...BAR }),
  box({ x0: 450, x1: 1210, z0: 162, z1: 180, ...BAR }),
  box({ x0: 1192, x1: 1210, z0: 18, z1: 162, ...BAR }),
]
const ARMS = [430, 1210].map((x0) => box({ x0, x1: x0 + 20, z0: 4, z1: 176, h0: 100, h1: 116 }))
/* Clamp knobs on the arms (moulded black). */
const KNOBS = [
  posts(5, 8, [
    [445.4, 569],
    [467, 509],
  ]),
  posts(5, 8, [
    [1225.4, 569],
    [1247, 509],
  ]),
]

function Arm({ index }) {
  const knobs = KNOBS[index]
  return (
    <g>
      <Box geo={ARMS[index]} />
      <path className="f-poly" d={knobs.body} />
      <path className="f-gS" d={`${knobs.band}${knobs.top}`} />
    </g>
  )
}

/* The ink bead: a ridge of white ink lying along the blade (stage units,
   origin at the front of its base). Its crest is lit; the half turning from
   the light is a roll band, darkest at its foot; a roll line cycles across
   it as it is pushed. */
const BEAD = 'M8 0A8 8 0 0 0 -7.52 -2.71L16.98 -70.71A8 8 0 0 1 32.5 -68Z'
const BEAD_ROLL = 'M0.24 -1.36L8 0L32.5 -68L24.74 -69.36Z'
const BEAD_SHADE = 'M8 0L32.5 -68L29.56 -69.06L5.06 -1.06Z'

function Bead() {
  return (
    <g transform="translate(663.96 589)">
      <g className="pp-bead">
        <path className="f-ink" d={BEAD} />
        <path className="f-aT" d={BEAD_ROLL} />
        <path className="f-dot" d={BEAD_SHADE} />
        <path className="l-roll pp-bead-roll" d="M2.2 -12.6L21.2 -65.2" />
      </g>
    </g>
  )
}

/**
 * The stencil in the mesh face: emulsion, the V's trailing slope, the open
 * scales with their ink residue, the flood coat and the V's leading slope
 * in it, the mesh threads over all of it, and the white ink that fills the
 * openings wherever the coat lies.
 */
function Stencil({ id }) {
  return (
    <g transform={MESH_FACE}>
      <rect className="f-si" x="-192" y="-62" width="724" height="144" />
      <g className="pp-vtrail">
        <rect className="f-siL" x="0" y="-58" width="1" height="124" />
      </g>
      {OPENINGS.map((u) => (
        <ScaleGlyph key={u} className="g-scale-open" transform={`translate(${u} 0)`} />
      ))}
      <g className="pp-coat">
        <rect className="f-coat" x="-192" y="-62" width="724" height="144" />
      </g>
      <g className="pp-vlead">
        <rect className="f-coatD" x="0" y="-58" width="1" height="124" />
      </g>
      <rect x="-192" y="-62" width="724" height="144" fill={`url(#${id}mesh)`} />
      <g className="pp-fill">
        <rect className="pp-bbox" x="-192" y="-62" width="724" height="144" />
        {OPENINGS.map((u) => (
          <ScaleGlyph key={u} className="g-scale-ink" transform={`translate(${u} 0)`} />
        ))}
      </g>
    </g>
  )
}

/** Everything of the screen behind the print head: left arm, stencil, bead, three bars. */
export function ScreenBack({ id }) {
  return (
    <g className="pp-frame">
      <Arm index={0} />
      <Stencil id={id} />
      <Bead />
      {BACK_BARS.map((geo) => (
        <Box key={geo.top} geo={geo} />
      ))}
    </g>
  )
}

/** The front bar and the right arm: in front of the blades. */
export function ScreenFront() {
  return (
    <g className="pp-frame">
      <Box geo={FRONT_BAR} />
      <path className="l-aS pp-hair" d="M470 593H1190" />
      <Arm index={1} />
    </g>
  )
}

/** Mesh threads, in the mesh face. */
export function MeshPattern({ id }) {
  return (
    <pattern id={`${id}mesh`} patternUnits="userSpaceOnUse" width="6" height="6">
      <path className="pp-thread-u" d="M0 3H6" />
      <path className="pp-thread-v" d="M3 0V6" />
    </pattern>
  )
}
